import React, { useState } from 'react';
import { 
  XCircle, 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  CheckCircle, 
  ChevronDown, 
  Lightbulb,
  Copy,
  Check
} from 'lucide-react';
import { Issue, Severity } from '../types';
import { t } from '../services/i18n';

interface IssueCardProps {
  issue: Issue;
  index: number;
}

const getSeverityStyle = (severity: Severity | string) => {
  // Normalize severity to enum value (handle string values from backend)
  let normalizedSeverity: Severity;
  if (typeof severity === 'string') {
    normalizedSeverity = severity.toUpperCase() as Severity;
  } else {
    normalizedSeverity = severity;
  }

  const styles: Record<string, {
    bg: string;
    border: string;
    iconBg: string;
    badge: string;
    icon: React.ReactNode;
  }> = {
    [Severity.CRITICAL]: {
      bg: 'bg-red-500/10',
      border: 'border-red-500',
      iconBg: 'bg-red-500/20',
      badge: 'bg-red-500 text-white',
      icon: <XCircle className="w-5 h-5 text-red-400" />
    },
    [Severity.HIGH]: {
      bg: 'bg-orange-500/10',
      border: 'border-orange-500',
      iconBg: 'bg-orange-500/20',
      badge: 'bg-orange-500 text-white',
      icon: <AlertTriangle className="w-5 h-5 text-orange-400" />
    },
    [Severity.MEDIUM]: {
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500',
      iconBg: 'bg-yellow-500/20',
      badge: 'bg-yellow-500 text-white',
      icon: <AlertCircle className="w-5 h-5 text-yellow-400" />
    },
    [Severity.LOW]: {
      bg: 'bg-blue-500/10',
      border: 'border-blue-500',
      iconBg: 'bg-blue-500/20',
      badge: 'bg-blue-500 text-white',
      icon: <Info className="w-5 h-5 text-blue-400" />
    },
    [Severity.INFO]: {
      bg: 'bg-slate-700/50',
      border: 'border-slate-500',
      iconBg: 'bg-slate-600',
      badge: 'bg-slate-500 text-white',
      icon: <CheckCircle className="w-5 h-5 text-slate-400" />
    }
  };

  // Return style or default to INFO if severity not found
  return styles[normalizedSeverity] || styles[Severity.INFO];
};

const getSeverityLabel = (severity: Severity | string): string => {
  // Normalize severity to enum value
  const normalizedSeverity = typeof severity === 'string' 
    ? severity.toUpperCase() as Severity 
    : severity;
    
  const labels: Record<string, string> = {
    [Severity.CRITICAL]: 'Critical',
    [Severity.HIGH]: 'High',
    [Severity.MEDIUM]: 'Medium',
    [Severity.LOW]: 'Low',
    [Severity.INFO]: 'Info'
  };
  return labels[normalizedSeverity] || 'Info';
};

export const IssueCard: React.FC<IssueCardProps> = ({ issue, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Ensure issue has a valid severity, default to INFO if missing
  const severity = issue.severity || Severity.INFO;
  const style = getSeverityStyle(severity);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (issue.snippet) {
      navigator.clipboard.writeText(issue.snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className={`
        rounded-xl border-l-4 p-5 transition-all
        ${style.bg}
        ${style.border}
        hover:shadow-lg transform hover:-translate-y-0.5
      `}
      style={{
        animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s both`
      }}
    >
      <div className="flex items-start gap-4">
        {/* Severity Icon */}
        <div className={`p-2 rounded-lg flex-shrink-0 ${style.iconBg}`}>
          {style.icon}
        </div>

        {/* Issue Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
            <h4 className="font-bold text-white text-lg">{issue.title}</h4>

            {/* Severity Badge */}
            <span className={`
              px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider flex-shrink-0
              ${style.badge}
            `}>
              {getSeverityLabel(severity)}
            </span>
          </div>

          <p className="text-slate-300 text-sm mb-3 leading-relaxed">
            {issue.description}
          </p>

          {/* Expandable Details */}
          <details 
            className="group"
            open={isExpanded}
            onToggle={(e) => setIsExpanded((e.target as HTMLDetailsElement).open)}
          >
            <summary className="cursor-pointer text-sm font-semibold text-indigo-400 hover:text-indigo-300 flex items-center gap-2 select-none">
              <span>{t('results.viewDetails') || 'View Details'}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </summary>

            <div className="mt-4 space-y-3 pl-4 border-l-2 border-slate-600">
              {/* Impact - Concise and Actionable */}
              {issue.impact && (
                <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="font-bold text-red-300 text-sm">{t('results.impact') || 'Impact'}</span>
                  </div>
                  <p className="text-red-200 text-xs leading-relaxed">
                    {issue.impact.length > 200 
                      ? `${issue.impact.substring(0, 200)}...` 
                      : issue.impact}
                  </p>
                </div>
              )}

              {/* Solution - Concise and Actionable */}
              {issue.fix && (
                <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Lightbulb className="w-4 h-4 text-emerald-400" />
                    <span className="font-bold text-emerald-300 text-sm">{t('results.howToFix') || 'How to Fix'}</span>
                  </div>
                  <p className="text-emerald-200 text-xs leading-relaxed">
                    {issue.fix.length > 250 
                      ? `${issue.fix.substring(0, 250)}...` 
                      : issue.fix}
                  </p>
                </div>
              )}

              {/* Code Snippet */}
              {issue.snippet && (
                <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-slate-300 text-xs">{t('results.codeExample') || 'Code Example'}</span>
                    <button
                      onClick={handleCopy}
                      className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors"
                      aria-label="Copy code"
                    >
                      {copied ? (
                        <Check className="w-3 h-3 text-emerald-400" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                    </button>
                  </div>
                  <pre className="text-xs text-slate-300 overflow-x-auto font-mono whitespace-pre-wrap break-words">
                    <code>{issue.snippet}</code>
                  </pre>
                </div>
              )}
            </div>
          </details>
        </div>
      </div>

      <style>{`
        @keyframes fadeInSlide {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

